import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';

/**
 * A generic drawer component backed by native <dialog>.
 * Slides in from bottom on mobile and from right on desktop.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {Component<Refs>}
 */
class GenericDrawer extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this.refs;
    dialog.addEventListener('cancel', this.#onCancel);
    dialog.addEventListener('click', this.#onDialogClick);
    dialog.addEventListener('close', this.#onNativeClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const { dialog } = this.refs;
    dialog.removeEventListener('cancel', this.#onCancel);
    dialog.removeEventListener('click', this.#onDialogClick);
    dialog.removeEventListener('close', this.#onNativeClose);

    this.#cancelPendingClose();
    this.#isClosing = false;
  }

  /**
   * @returns {boolean} Whether the drawer is open
   */
  get isOpen() {
    return this.refs.dialog.open;
  }

  /**
   * Toggle the drawer open/closed
   * @param {Event} [event]
   */
  toggle(event) {
    event?.preventDefault();
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the drawer
   */
  open() {
    const { dialog } = this.refs;

    if (dialog.open && !this.#isClosing) {
      return;
    }

    this.#cancelPendingClose();
    this.#isClosing = false;
    dialog.classList.remove('drawer-closing');

    const trigger = this.querySelector('.drawer__trigger');

    if (!dialog.open) {
      dialog.showModal();
    }

    if (trigger) {
      trigger.setAttribute('aria-expanded', 'true');
    }

    document.body.classList.add('drawer-open');
    document.documentElement.setAttribute('scroll-lock', '');

    requestAnimationFrame(() => {
      dialog.classList.add('drawer-open');
      const panel = dialog.querySelector('.drawer__panel');
      trapFocus(panel || dialog);
    });

    this.dispatchEvent(
      new CustomEvent('drawer:open', {
        bubbles: true,
        detail: { drawer: this },
      })
    );
  }

  /**
   * Close the drawer
   */
  close() {
    const { dialog } = this.refs;

    if (!dialog.open || this.#isClosing) return;

    this.#isClosing = true;

    const trigger = this.querySelector('.drawer__trigger');
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
    }

    dialog.classList.remove('drawer-open');
    dialog.classList.add('drawer-closing');

    removeTrapFocus();

    this.#closeTimer = setTimeout(() => {
      dialog.close();
      dialog.classList.remove('drawer-closing');
      this.#isClosing = false;
      this.#closeTimer = null;
      this.#unlockPageIfNeeded();
    }, 300);

    this.dispatchEvent(
      new CustomEvent('drawer:close', {
        bubbles: true,
        detail: { drawer: this },
      })
    );
  }

  /** @type {boolean} */
  #isClosing = false;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #closeTimer = null;

  #cancelPendingClose() {
    if (this.#closeTimer) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }
  }

  #onCancel = (event) => {
    event.preventDefault();
    this.close();
  };

  #onDialogClick = (event) => {
    if (event.target === this.refs.dialog) {
      this.close();
    }
  };

  #onNativeClose = () => {
    this.#isClosing = false;
    this.#cancelPendingClose();
    this.refs.dialog.classList.remove('drawer-open', 'drawer-closing');
    this.#unlockPageIfNeeded();
  };

  #unlockPageIfNeeded() {
    if (document.querySelector('dialog[open][scroll-lock]')) return;

    document.body.classList.remove('drawer-open');
    document.documentElement.removeAttribute('scroll-lock');
  }
}

if (!customElements.get('generic-drawer')) {
  customElements.define('generic-drawer', GenericDrawer);
}
